// EmojiReactions.tsx
import { Box, IconButton, Popover, Tooltip } from "@mui/material";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import { useState, MouseEvent, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import { selectUserDetails } from "../../../features/userSlice";

type Reaction = {
  id: string;
  native: string;
  userIds: (string | number)[];
  removedUserIds?: (string | number)[]; // Track users who explicitly removed their reaction
};

interface EmojiReactionsProps {
  reactions: Reaction[];
  onReact: (reactionId: string, native: string, isRemoving: boolean) => void;
  currentUserId: string;
  particularReaction: any;
  commentDetails: any;
}

const EmojiReactions = ({
  reactions,
  onReact,
  currentUserId,
  particularReaction,
  commentDetails,
}: EmojiReactionsProps) => {
  const selectUserDetail = useSelector(selectUserDetails);
  const currentUserName = selectUserDetail
    ? `${selectUserDetail.first_name} ${selectUserDetail.last_name}`.trim()
    : "";
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { reactionTrackID } = useSelector(
    (state: any) => state.genericCommentsAndFiles
  );
  const [serverReactions, setServerReactions] = useState<any[]>([]);
  const open = Boolean(anchorEl);

  const handleOpen = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };
  /**
   * Syncs and accumulates server reactions across polling cycles.
   *
   * Data sources:
   * - `commentDetails.reactions`:
   *    - If present and `[]`, it means "this comment currently has NO reactions"
   *      → clear `serverReactions`.
   * - `particularReaction`:
   *    - Represents *incremental* reaction rows since last modified timestamp.
   *    - If non-empty:
   *        - If it looks like a strict subset of previous keys → treat as
   *          "full updated list after deletion" and REPLACE `serverReactions`.
   *        - Otherwise → union previous `serverReactions` with new rows.
   *
   * This ensures:
   * - Real-time additions are accumulated.
   * - Full delete of last reaction (on any device) clears the UI.
   */
  useEffect(() => {
    const hasCommentReactionsArray =
      commentDetails && Array.isArray(commentDetails.reactions);
    // Case 1: backend says this comment currently has ZERO reactions
    if (hasCommentReactionsArray && commentDetails!.reactions!.length === 0) {
      setServerReactions([]);
      return;
    }
    // Case 2: no incremental rows this poll → keep previous accumulated state
    if (!Array.isArray(particularReaction) || particularReaction.length === 0) {
      return;
    }
    // Case 3: apply incremental or full updates from particularReaction
    setServerReactions((prev) => {
      if (prev.length === 0) {
        return particularReaction;
      }

      const keyOf = (row: any) => `${row.reaction_id}::${row.user_id}`;

      const prevKeys = new Set(prev.map(keyOf));
      const newKeys = new Set(particularReaction.map(keyOf));

      let isStrictSubset = true;
      for (const k of newKeys) {
        if (!prevKeys.has(k)) {
          isStrictSubset = false;
          break;
        }
      }

      if (isStrictSubset && newKeys.size < prevKeys.size) {
        return particularReaction;
      }
      const map = new Map<string, any>();
      for (const row of prev) {
        map.set(keyOf(row), row);
      }
      for (const row of particularReaction) {
        map.set(keyOf(row), row); // overwrite or add new
      }
      return Array.from(map.values());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [particularReaction, commentDetails?.reactions]);

  const handleClose = () => {
    setAnchorEl(null);
  };
  /**
   * Called when a user selects an emoji from the picker.
   * Triggers onReact() with appropriate reaction id and add/remove flag.
   * @param {any} emojiData - The emoji object from emoji-mart library.
   * @example
   * handleSelectEmoji({ id: "heart", native: "❤️" });
   * → calls onReact("heart", "❤️", false) if user hasn't reacted
   */
  const handleSelectEmoji = (emojiData: any) => {
    const reactionId = emojiData.id;
    const native = emojiData.native;
    const existing = (mergedReactionsMap as Record<string, Reaction>)[
      reactionId
    ];
    const isReacted =
      !!existing &&
      existing.userIds.map(String).includes(String(currentUserId));
    onReact(reactionId, native, isReacted);
    handleClose();
  };
  /**
   * Returns the native emoji character (e.g. "😂") from its emoji id.
   * Falls back gracefully (to the id) if the emoji is not found.
   * @param {string} id - The emoji shortname or id.
   * @returns {string} - The native emoji character.
   * @example
   * getNativeFromEmojiId("heart"); // returns "❤️"
   * getNativeFromEmojiId("non_existing"); // returns "non_existing"
   */
  const getNativeFromEmojiId = (id: string): string => {
    try {
      const emojiEntry = (data as any)?.emojis?.[id];
      if (!emojiEntry) return id;
      if (emojiEntry.native) return emojiEntry.native;
      if (
        emojiEntry.skins &&
        emojiEntry.skins.length > 0 &&
        emojiEntry.skins[0].native
      )
        return emojiEntry.skins[0].native;
      return id;
    } catch (e) {
      return id;
    }
  };
  /**
   * Builds a map of reactions from backend data (serverReactions).
   * Groups multiple DB rows by reaction_id and collects their userIds.
   * @returns {Record<string, Reaction>} - A map of reactionId → Reaction object.
   * @example
   * serverReactions = [
   *   { reaction_id: "smile", user_id: 50 },
   *   { reaction_id: "smile", user_id: 51 },
   *   { reaction_id: "heart", user_id: 52 }
   * ];
   * buildDbReactionsMap();
   *  returns:
   *  {
   *    "smile": { id: "smile", native: "😄", userIds: [50, 51] },
   *   "heart": { id: "heart", native: "❤️", userIds: [52] }
   * }
   */
  const buildDbReactionsMap = () => {
    const map: Record<string, Reaction> = {};
    if (!Array.isArray(serverReactions)) return map;
    for (const row of serverReactions) {
      const id = String(row.reaction_id);
      const userId = row.user_id;
      if (!map[id]) {
        map[id] = {
          id,
          native: getNativeFromEmojiId(id),
          userIds: [],
        };
      }
      map[id].userIds.push(userId);
    }

    for (const k of Object.keys(map)) {
      map[k].userIds = Array.from(new Set(map[k].userIds.map(String))).map(
        (s) => (isNaN(Number(s)) ? s : Number(s))
      );
    }
    return map;
  };
  /**
   * Builds a map of local reactions (optimistic UI state).
   * @returns {Record<string, Reaction>} - Map of local reactions.
   * @example
   * reactions = [
   *   { id: "laugh", native: "😂", userIds: [50, 51], removedUserIds: [52] }
   * ];
   * buildLocalReactionsMap();
   *  returns:
   *  {
   *   "laugh": { id: "laugh", native: "😂", userIds: [50, 51], removedUserIds: [52] }
   * }
   */
  const buildLocalReactionsMap = () => {
    const map: Record<string, Reaction> = {};
    for (const r of reactions || []) {
      if (!r || !r.id) continue;
      map[String(r.id)] = {
        id: String(r.id),
        native: r.native || getNativeFromEmojiId(String(r.id)),
        userIds: Array.isArray(r.userIds) ? r.userIds : [],
        removedUserIds: r.removedUserIds || [],
      };
    }
    return map;
  };
  /**
   * Merges backend (DB) reactions with local (optimistic) reactions.
   * - Unions userIds from both sources.
   * - Removes any IDs present in local.removedUserIds.
   * @returns {Record<string, Reaction>} - Fully merged reaction map.
   * @example
   * DB (from backend)
   * dbMap = {
   *   "laugh": { id: "laugh", native: "😂", userIds: [50, 51] }
   * };
   * Local optimistic (unsynced)
   * localMap = {
   *   "laugh": { id: "laugh", native: "😂", userIds: [52], removedUserIds: [50] }
   * };
   * mergeReactions();
   * returns:
   * {
   *   "laugh": { id: "laugh", native: "😂", userIds: [51, 52], removedUserIds: [50] }
   * }
   */
  const mergeReactions = () => {
    const dbMap = buildDbReactionsMap();
    const localMap = buildLocalReactionsMap();
    const merged: Record<string, Reaction> = {};

    const allIds = new Set([...Object.keys(dbMap), ...Object.keys(localMap)]);

    for (const id of allIds) {
      const dbReaction = dbMap[id];
      const localReaction = localMap[id];

      if (!localReaction) {
        merged[id] = dbReaction;
      } else if (!dbReaction) {
        merged[id] = localReaction;
      } else {
        const dbUserIds = dbReaction.userIds.map(String);
        const localUserIds = localReaction.userIds.map(String);
        const removedUserIds = (localReaction.removedUserIds || []).map(String);

        let finalUserIds = [...dbUserIds];
        for (const userId of localUserIds) {
          if (!finalUserIds.includes(userId)) {
            finalUserIds.push(userId);
          }
        }

        finalUserIds = finalUserIds.filter(
          (userId) => !removedUserIds.includes(userId)
        );

        const finalUserIdsTyped = finalUserIds.map((s) =>
          isNaN(Number(s)) ? s : Number(s)
        );

        merged[id] = {
          id,
          native: localReaction.native || dbReaction.native,
          userIds: finalUserIdsTyped,
          removedUserIds: localReaction.removedUserIds,
        };
      }
    }

    return merged;
  };

  const reactionsDeps = useMemo(() => reactions, [reactions]);

  const mergedReactionsMap = useMemo(() => {
    return mergeReactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reactionsDeps, serverReactions]);

  /**
   * Computes final ordered list of reactions for rendering.
   * Ensures DB reactions appear first (in original order),
   * followed by locally added-only reactions.
   * @returns {Reaction[]} - Ordered array of merged reactions.
   * @example
   * mergedReactionsMap = {
   *   "smile": { id: "smile", native: "😄", userIds: [50, 51] },
   *   "heart": { id: "heart", native: "❤️", userIds: [52] }
   * };
   *
   * ordered = ["smile", "heart"];
   */
  const ordered = useMemo(() => {
    const dbKeys = Object.keys(buildDbReactionsMap());
    const mergedKeys = Object.keys(mergedReactionsMap);
    const orderedKeys = dbKeys.filter(
      (k) => mergedReactionsMap[k] && mergedReactionsMap[k].userIds.length > 0
    );

    const localOnly = mergedKeys.filter(
      (k) =>
        !dbKeys.includes(k) &&
        mergedReactionsMap[k] &&
        mergedReactionsMap[k].userIds.length > 0
    );
    return [...orderedKeys, ...localOnly].map((k) => mergedReactionsMap[k]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergedReactionsMap, serverReactions]);

  /**
   * Builds a map of `reaction_id → [user names]` for emoji tooltips.
   *
   * - Combines backend `particularReaction` data with local optimistic `reactions`.
   * - Adds current user's name instantly on new reaction.
   * - Removes it on optimistic unreact.
   * @returns {Record<string, string[]>} Map of reaction id → array of unique user names.
   * @example
   * particularReaction = [
   *   { reaction_id: "hugging_face", user_id: 50, user: "Name1" }
   * ];
   * reactions = [
   *   { id: "hugging_face", userIds: [50, 60] } // 60 = current user
   * ];
   * currentUserId = 60;
   * currentUserName = "Name2";
   *
   * Result:
   * { hugging_face: ["Name1", "Name2"] }
   */
  const reactionUserNamesMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    if (Array.isArray(serverReactions)) {
      for (const row of serverReactions) {
        const rid = String(row.reaction_id);
        const name = String(row.user || row.name || "");
        if (!map[rid]) map[rid] = [];
        if (name && !map[rid].includes(name)) map[rid].push(name);
      }
    }

    if (Array.isArray(reactions)) {
      for (const r of reactions) {
        if (!r || !r.id) continue;
        const rid = String(r.id);
        if (!map[rid]) map[rid] = [];
        if (
          r.userIds &&
          r.userIds.map(String).includes(String(currentUserId))
        ) {
          if (currentUserName && !map[rid].includes(currentUserName)) {
            map[rid].push(currentUserName);
          }
        }
        if ((r as any).users && Array.isArray((r as any).users)) {
          for (const u of (r as any).users) {
            const nm = u?.name || u?.user;
            if (nm && !map[rid].includes(nm)) map[rid].push(nm);
          }
        }
        if (
          (r as any).removedUserIds &&
          Array.isArray((r as any).removedUserIds)
        ) {
          for (const rem of (r as any).removedUserIds) {
            if (String(rem) === String(currentUserId) && currentUserName) {
              map[rid] = map[rid].filter((n) => n !== currentUserName);
            }
          }
        }
      }
    }
    for (const k of Object.keys(map)) {
      map[k] = Array.from(new Set(map[k]));
    }

    return map;
  }, [serverReactions, reactions, currentUserId, currentUserName]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      {/* Display existing emoji reactions */}
      {ordered.map(({ id, native, userIds }) => {
        const isReacted = userIds.map(String).includes(String(currentUserId));
        const rawNames = reactionUserNamesMap[id] || [];
        const displayNames = rawNames.map((n) =>
          String(n) === String(currentUserName) ? "You" : n
        );
        //"You" appears first if present
        displayNames.sort((a, b) => (a === "You" ? -1 : b === "You" ? 1 : 0));

        const tooltipTitle =
          displayNames.length > 0
            ? displayNames.join(", ")
            : `${userIds.length} reaction(s)`;
        return (
          <Tooltip key={id} title={tooltipTitle} arrow placement="top">
            <Box
              key={id}
              onClick={() => onReact(id, native, isReacted)}
              sx={{
                cursor: "pointer",
                px: 1,
                py: 0.3,
                fontSize: 14,
                display: "inline-flex",
                alignItems: "center",
                borderRadius: "12px",
                border: `1px solid ${isReacted ? "#9c27b0" : "#ddd"}`,
                backgroundColor: isReacted ? "#9C27B00A" : "#f5f5f5",
                color: isReacted ? "#9c27b0" : "inherit",
                transition: "all 0.2s",
              }}
            >
              {native} {userIds.length}
            </Box>
          </Tooltip>
        );
      })}

      {/* Reaction picker button */}
      <IconButton
        size="small"
        data-tracker={reactionTrackID}
        onClick={handleOpen}
        sx={{ p: 0 }}
      >
        <EmojiEmotionsIcon fontSize="small" />
      </IconButton>

      {/* Full Emoji Mart picker */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3,
            overflow: "hidden",
          },
        }}
      >
        <Picker
          data={data}
          onEmojiSelect={handleSelectEmoji}
          theme="light"
          previewPosition="none"
          emojiSize={22}
          perLine={9}
        />
      </Popover>
    </Box>
  );
};

export default EmojiReactions;
